// Single source of truth for auth persistence + the 48h absolute-session
// lifecycle. Framework-agnostic on purpose: both the React AuthProvider
// (Auth.js) and the plain axios interceptor (api/api.js) need to read/clear
// the session and trigger a forced logout, and only one of those is a React
// component.
const STORAGE_KEY = 'currentUser';
const LEGACY_TOKEN_KEY = 'token'; // dropped on every clear, no longer written
const EXPIRED_FLAG_KEY = 'auth_expired_reason';
const LOGOUT_EVENT = 'auth:logout';
const REFRESHED_EVENT = 'auth:refreshed';

let logoutTimerId = null;
let hasForcedLogout = false;

export function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.user || !parsed?.accessToken || !parsed?.sessionExpiresAt) return null;

    return parsed;
  } catch {
    return null;
  }
}

export function saveSession({ user, accessToken, sessionExpiresAt }) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, accessToken, sessionExpiresAt }));
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
}

export function getAccessToken() {
  return loadSession()?.accessToken || null;
}

export function isSessionExpired(session = loadSession()) {
  if (!session) return true;
  return Date.now() >= session.sessionExpiresAt;
}

export function clearLogoutTimer() {
  if (logoutTimerId) {
    clearTimeout(logoutTimerId);
    logoutTimerId = null;
  }
}

// A 48h delay is comfortably under the ~24.8-day (2^31 ms) setTimeout cap,
// so a single timer is safe here — no need for interval-based re-arming.
export function scheduleAbsoluteLogout(sessionExpiresAt, onExpire) {
  clearLogoutTimer();

  const delay = sessionExpiresAt - Date.now();
  if (delay <= 0) {
    onExpire();
    return;
  }

  logoutTimerId = setTimeout(onExpire, delay);
}

// Resets the one-shot "already forced a logout" guard. Test-only: production
// code never needs to force a *second* logout in the same page life — the
// hard redirect in performLogout() below ends the page anyway.
export function __resetForTests() {
  hasForcedLogout = false;
  clearLogoutTimer();
}

// Clears everything, optionally flags the login page to show the
// session-expired banner once, and hard-navigates to /login. A real browser
// navigation (not client-side routing) is deliberate: it unmounts the whole
// React tree, which closes any open modal (registration/payment/refund) for
// free and guarantees every route guard re-evaluates from a clean slate —
// there is no path back to stale protected content after this runs.
export function performLogout({ expired = false } = {}) {
  if (hasForcedLogout) return;
  hasForcedLogout = true;

  clearLogoutTimer();
  clearSession();

  if (expired) {
    try {
      sessionStorage.setItem(EXPIRED_FLAG_KEY, 'expired');
    } catch {
      // sessionStorage can throw in locked-down/private-browsing contexts —
      // losing the one-time banner is an acceptable degradation, logout
      // itself must still proceed.
    }
  }

  try {
    window.dispatchEvent(new CustomEvent(LOGOUT_EVENT, { detail: { expired } }));
  } catch {
    // ignore — non-browser test environments without CustomEvent support
  }

  window.location.href = '/login';
}

export function forceLogout() {
  performLogout({ expired: true });
}

export function consumeExpiredReason() {
  try {
    const reason = sessionStorage.getItem(EXPIRED_FLAG_KEY);
    if (reason) sessionStorage.removeItem(EXPIRED_FLAG_KEY);
    return reason;
  } catch {
    return null;
  }
}

// Cross-tab sync: when one tab clears the session key, every other tab gets
// a native `storage` event (it never fires in the tab that made the change,
// which is exactly what we want here).
export function onCrossTabLogout(handler) {
  const listener = (event) => {
    if (event.key === STORAGE_KEY && event.newValue === null) {
      handler();
    }
  };

  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}

export function emitRefreshed(user) {
  try {
    window.dispatchEvent(new CustomEvent(REFRESHED_EVENT, { detail: { user } }));
  } catch {
    // ignore
  }
}

export function onRefreshed(handler) {
  window.addEventListener(REFRESHED_EVENT, handler);
  return () => window.removeEventListener(REFRESHED_EVENT, handler);
}

export function onLogoutEvent(handler) {
  window.addEventListener(LOGOUT_EVENT, handler);
  return () => window.removeEventListener(LOGOUT_EVENT, handler);
}
